import { useMemo } from 'react'

import { errorView, useWindow } from '@krp/client-runtime'
import {
  AQSI_WINDOWS,
  TRACE_DEEP_POLL_MS,
  TraceScreen,
  aqsiSetTraceDeep,
  aqsiTraceDeep,
  aqsiTraceRegisterCheck,
  aqsiTraceSale,
  aqsiTraceSales,
  buildTraceVm,
  type TraceFailureVm,
  type TraceScreenActions,
} from '@krp/resource-aqsi-ui'

import { useAppSelector } from '../../app/hooks'

function failureOf(error: unknown): TraceFailureVm | null {
  if (error === undefined) return null
  const view = errorView(error)
  return { message: view.message, meta: `Код: ${view.code} · запрос ${view.correlationId ?? '—'}` }
}

/**
 * Binding окна «Диагностика продаж» (rid и выбранная продажа — из адреса): список последних продаж кабинета, разбор выбранной,
 * переключатель подробного режима и контрольный замер кассы. Замер — команда кассе (пять служебных чтений), уходит только по
 * подтверждению человека на экране.
 */
export function AqsiTraceBinding() {
  const window = useWindow()
  const resourceId = window.params.rid ?? ''
  const saleId = window.params.saleId ?? null

  const listArgs = useMemo(() => ({ resourceId }), [resourceId])
  const saleArgs = useMemo(() => ({ resourceId, saleId: saleId ?? '' }), [resourceId, saleId])
  const list = aqsiTraceSales.useQuerySubscription(listArgs, { skip: resourceId === '', refetchOnMountOrArgChange: true })
  const sale = aqsiTraceSale.useQuerySubscription(saleArgs, { skip: resourceId === '' || saleId === null, refetchOnMountOrArgChange: true })
  // Флаг подробного режима перечитывается раз в минуту, пока окно открыто; после «Включить» запрос перечитывается по тегу.
  aqsiTraceDeep.useQuerySubscription(listArgs, { skip: resourceId === '', pollingInterval: TRACE_DEEP_POLL_MS })

  const selectList = useMemo(() => aqsiTraceSales.select(listArgs), [listArgs])
  const selectSale = useMemo(() => aqsiTraceSale.select(saleArgs), [saleArgs])
  const selectDeep = useMemo(() => aqsiTraceDeep.select(listArgs), [listArgs])
  const listResult = useAppSelector(selectList)
  const saleResult = useAppSelector(selectSale)
  const deepResult = useAppSelector(selectDeep)

  const [setDeep, deepMutation] = aqsiSetTraceDeep.useMutation()
  const [runCheck, checkMutation] = aqsiTraceRegisterCheck.useMutation()
  const deepError = useMemo(() => failureOf(deepMutation.error), [deepMutation.error])
  const checkError = useMemo(() => failureOf(checkMutation.error), [checkMutation.error])
  const deepState = deepResult.data ?? null
  const checkData = checkMutation.data ?? null
  const deepBusy = deepMutation.isLoading
  const checkBusy = checkMutation.isLoading

  const vm = useMemo(
    () =>
      buildTraceVm({
        sales: listResult,
        sale: saleId === null ? undefined : saleResult,
        saleId,
        tools: { deep: deepState, deepBusy, deepError, check: checkData, checkBusy, checkError },
      }),
    [listResult, saleResult, saleId, deepState, deepBusy, deepError, checkData, checkBusy, checkError],
  )
  const { refetch: refetchList } = list
  const { refetch: refetchSale } = sale
  const actions = useMemo<TraceScreenActions>(
    () => ({
      select: (selected) => window.replaceWith(AQSI_WINDOWS.trace, { rid: resourceId, saleId: selected }),
      closeSale: () => window.replaceWith(AQSI_WINDOWS.trace, { rid: resourceId }),
      reloadList: () => void refetchList(),
      reloadSale: () => void refetchSale(),
      setDeep: (enabled) => void setDeep({ resourceId, enabled }),
      runRegisterCheck: () => void runCheck({ resourceId }),
    }),
    [refetchList, refetchSale, resourceId, runCheck, setDeep, window],
  )
  return <TraceScreen vm={vm} actions={actions} />
}
